import React, { useState } from 'react';
import { 
    Paper, TableRow, TableCell, Checkbox, Chip, Typography, Button,
    Box, FormControl, Select, MenuItem, Autocomplete, TextField, Tooltip, IconButton, FormControlLabel, Stack, Divider
} from '@mui/material';
import { 
    Sort as SortIcon, DoneAll as DoneAllIcon, RemoveDone as RemoveDoneIcon, MailOutline as MailOutlineIcon 
} from '@mui/icons-material';
import { format, startOfMonth, endOfMonth, addDays } from 'date-fns';
import DataTable from '../DataTable';
import CustomDatePicker from '../CustomDatePicker';
import { formatDateDisplay } from '../../utils/dateUtils';
import ConfirmDialog from '../ConfirmDialog';
import TransportOrderDialog from './TransportOrderDialog'; 
import * as API from '../../api'; 

// --- HẰNG SỐ ---
const TRANSPORT_OPS_LABELS = { 'OPEN': 'Chờ ĐH xe', 'CONTACTED': 'Đã báo xe', 'CONFIRMED': 'Đã xác nhận', 'DONE': 'Hoàn tất' };
const TRANSPORT_OPS_COLORS = { 'OPEN': 'error', 'CONTACTED': 'warning', 'CONFIRMED': 'info', 'DONE': 'success' };
const DIRECTION_LABELS = { 'ONE_WAY_TO': 'Chiều đi', 'ONE_WAY_FROM': 'Chiều về', 'ROUND_TRIP': 'Hai chiều' };
const DEFAULT_TRANSPORT_STATUS = ['OPEN', 'CONTACTED', 'CONFIRMED']; // Mặc định ẩn các xe đã hoàn tất (DONE)

const CustomLabel = ({ title }) => (
    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5, color: '#2c3e50', fontSize: '0.9rem' }}>
        {title}
    </Typography>
);

const TransportOpsTab = ({ transports, handleViewBooking, onReload, showToast }) => {
    
    // --- 1. STATE BỘ LỌC (GIAO DIỆN) ---
    const [quickFilterMode, setQuickFilterMode] = useState('CUSTOM');
    const [filterStartDate, setFilterStartDate] = useState(format(startOfMonth(new Date()), 'yyyy-MM-dd'));
    const [filterEndDate, setFilterEndDate] = useState(format(endOfMonth(new Date()), 'yyyy-MM-dd'));
    const [isAllTime, setIsAllTime] = useState(false);
    const [filterTransportStatus, setFilterTransportStatus] = useState(DEFAULT_TRANSPORT_STATUS);
    const [filterTransportSupplier, setFilterTransportSupplier] = useState(null);
    const [sortOrder, setSortOrder] = useState('asc');

    // --- 2. STATE ACTIVE FILTER (ÁP DỤNG LỌC) ---
    const [activeFilters, setActiveFilters] = useState({
        startDate: format(startOfMonth(new Date()), 'yyyy-MM-dd'),
        endDate: format(endOfMonth(new Date()), 'yyyy-MM-dd'),
        transportStatus: DEFAULT_TRANSPORT_STATUS,
        allTime: false,
        sort: 'asc',
        transportSupplier: null
    });

    // --- 3. STATE THAO TÁC HÀNG LOẠT & DIALOG ---
    const [selectedIds, setSelectedIds] = useState([]); 
    const [confirmModal, setConfirmModal] = useState({ open: false, title: '', content: '', action: null, confirmColor: 'primary' });
    
    // State cho Lệnh đặt xe
    const [isTransportOrderOpen, setIsTransportOrderOpen] = useState(false);
    const [selectedTransportsForOrder, setSelectedTransportsForOrder] = useState([]);

    // --- HÀM XỬ LÝ SỰ KIỆN GIAO DIỆN BỘ LỌC ---
    const handleQuickFilterChange = (event) => {
        const mode = event.target.value;
        setQuickFilterMode(mode);
        if (mode === 'CUSTOM') return;
        const today = new Date(); let start, end;
        if (mode === 'TODAY') { start = format(today, 'yyyy-MM-dd'); end = format(today, 'yyyy-MM-dd'); }
        else if (mode === 'TOMORROW') { const tmr = addDays(today, 1); start = format(tmr, 'yyyy-MM-dd'); end = format(tmr, 'yyyy-MM-dd'); }
        else if (mode === 'NEXT_3_DAYS') { start = format(today, 'yyyy-MM-dd'); end = format(addDays(today, 3), 'yyyy-MM-dd'); }
        setFilterStartDate(start); setFilterEndDate(end); setIsAllTime(false);
    };

    const handleApplyFilter = () => {
        setActiveFilters({
            startDate: filterStartDate, endDate: filterEndDate, transportStatus: filterTransportStatus,
            allTime: isAllTime, sort: sortOrder, transportSupplier: filterTransportSupplier
        });
        setSelectedIds([]); 
    };

    const toggleSort = () => {
        const newSort = sortOrder === 'asc' ? 'desc' : 'asc';
        setSortOrder(newSort);
        setActiveFilters(prev => ({ ...prev, sort: newSort }));
    };

    const handleToggleAllTransportStatus = () => {
        const allKeys = Object.keys(TRANSPORT_OPS_LABELS);
        if (filterTransportStatus.length === allKeys.length) setFilterTransportStatus([]);
        else setFilterTransportStatus(allKeys);
    };

    const getTransportSupplierOptions = () => {
        const suppliersMap = new Map();
        transports.forEach(t => {
            if (t.supplier) {
                const id = typeof t.supplier === 'object' ? t.supplier.id : t.supplier;
                const name = t.supplier_name || 'Nhà xe';
                if (!suppliersMap.has(id)) suppliersMap.set(id, { id, name });
            }
        });
        return Array.from(suppliersMap.values());
    };

    // --- LOGIC LỌC DỮ LIỆU ---
    const getFilteredTransports = () => {
        // CHỈ XỬ LÝ XE CÓ PAYMENT_METHOD === 'SEPARATE'
        return transports.filter(t => t.payment_method === 'SEPARATE').filter(t => {
            
            const isMissingDate = !t.pickup_datetime;

            // 1. Lọc thời gian (Bỏ qua nếu chưa cài ngày giờ đón)
            if (!activeFilters.allTime && !isMissingDate) {
                const dateToCheck = t.pickup_datetime.split('T')[0];
                if (dateToCheck < activeFilters.startDate || dateToCheck > activeFilters.endDate) return false;
            }

            // 2. Lọc trạng thái điều hành
            const opsStt = t.operation_status || 'OPEN';
            if (activeFilters.transportStatus.length > 0 && !activeFilters.transportStatus.includes(opsStt)) return false;

            // 3. Lọc theo Nhà xe
            if (activeFilters.transportSupplier) {
                const sId = typeof t.supplier === 'object' ? t.supplier?.id : t.supplier;
                if (sId !== activeFilters.transportSupplier.id) return false;
            }

            return true;
        }).sort((a, b) => {
            // Sắp xếp: Xe chưa xếp lịch lên đầu, sau đó sắp xếp theo ngày
            const noDateA = !a.pickup_datetime;
            const noDateB = !b.pickup_datetime;
            if (noDateA && !noDateB) return -1;
            if (!noDateA && noDateB) return 1;

            const timeA = a.pickup_datetime ? new Date(a.pickup_datetime).getTime() : 0;
            const timeB = b.pickup_datetime ? new Date(b.pickup_datetime).getTime() : 0;
            return activeFilters.sort === 'asc' ? timeA - timeB : timeB - timeA;
        });
    };

    const displayData = getFilteredTransports();

    // --- THAO TÁC BẢNG & HÀNG LOẠT ---
    const handleSelectRow = (id) => {
        setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };

    const handleSelectAll = (e) => {
        if (e.target.checked) setSelectedIds(displayData.map(row => row.id));
        else setSelectedIds([]);
    };

    const closeConfirmModal = () => setConfirmModal({ ...confirmModal, open: false });
    const handleConfirmAction = () => {
        if (confirmModal.action) confirmModal.action();
        closeConfirmModal();
    };

    const executeBulkUpdate = async (newStatus) => {
        try {
            const updatePromises = selectedIds.map(id => 
                API.client.patch(`/transports/${id}/update_status/`, { operation_status: newStatus })
            );
            await Promise.all(updatePromises);

            if (showToast) showToast("Cập nhật trạng thái Xe thành công!", "success");
            setSelectedIds([]); 
            if (onReload) onReload(); 
        } catch (error) {
            console.error("Lỗi cập nhật:", error);
            if (showToast) showToast("Có lỗi xảy ra khi cập nhật trạng thái.", "error");
        }
    };

    const handleBulkUpdateStatus = (newStatus) => {
        setConfirmModal({
            open: true,
            title: 'Xác nhận cập nhật',
            content: `Cập nhật trạng thái ${TRANSPORT_OPS_LABELS[newStatus]} cho ${selectedIds.length} chuyến xe đang chọn?`,
            confirmColor: TRANSPORT_OPS_COLORS[newStatus] || 'primary',
            action: () => executeBulkUpdate(newStatus)
        });
    };

    const handleOpenBulkTransportOrder = () => {
        const selectedObjs = transports.filter(t => selectedIds.includes(t.id));
        
        // Kiểm tra xem có khác nhà xe không
        const firstSupplier = selectedObjs[0]?.supplier;
        const isSameSupplier = selectedObjs.every(t => {
            const s1 = typeof t.supplier === 'object' ? t.supplier?.id : t.supplier;
            const s2 = typeof firstSupplier === 'object' ? firstSupplier?.id : firstSupplier;
            return s1 === s2;
        });

        if (!isSameSupplier) {
            if(showToast) showToast("Lỗi: Không thể gửi lệnh gộp cho các chuyến xe của nhà xe KHÁC NHAU!", "error");
            return;
        }

        if (!firstSupplier) {
            if(showToast) showToast("Lỗi: Các chuyến xe được chọn chưa được gán Nhà cung cấp!", "error");
            return;
        }
        
        setSelectedTransportsForOrder(selectedObjs);
        setIsTransportOrderOpen(true);
    };

    return (
        <Paper sx={{ p: 3, borderRadius: 2 }}>
            
            {/* === KHU VỰC BỘ LỌC TƯƠNG TỰ DASHBOARD CŨ === */}
            <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', width: '100%', mb: 2 }}>
                    <FormControl size="small" sx={{ width: '160px', pt: 3.2 }}>
                        <Select value={quickFilterMode} onChange={handleQuickFilterChange} displayEmpty>
                            <MenuItem value="CUSTOM">-- Lọc nhanh --</MenuItem>
                            <MenuItem value="TODAY">Hôm nay</MenuItem>
                            <MenuItem value="TOMORROW">Ngày mai</MenuItem>
                            <MenuItem value="NEXT_3_DAYS">3 ngày tới</MenuItem>
                        </Select>
                    </FormControl>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Box style={{ width: '160px' }}>
                            <CustomLabel title="Từ ngày đón" />
                            <CustomDatePicker value={filterStartDate} disabled={isAllTime || quickFilterMode !== 'CUSTOM'} onChange={(e) => setFilterStartDate(e.target.value)} />
                        </Box>
                        <Box style={{ width: '160px' }}>
                            <CustomLabel title="Đến ngày đón" />
                            <CustomDatePicker value={filterEndDate} disabled={isAllTime || quickFilterMode !== 'CUSTOM'} onChange={(e) => setFilterEndDate(e.target.value)} />
                        </Box>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <CustomLabel title="Lọc theo Nhà xe" />
                        <Autocomplete 
                            size="small" fullWidth 
                            options={getTransportSupplierOptions()} getOptionLabel={(option) => option.name || ''} 
                            value={filterTransportSupplier} onChange={(e, newValue) => setFilterTransportSupplier(newValue)} 
                            renderInput={(params) => <TextField {...params} placeholder="Chọn nhà xe..." />} 
                        />
                    </Box>
                    <Box sx={{ pt: 3.2 }}>
                        <Tooltip title={`Sắp xếp ngày đón: ${sortOrder === 'asc' ? 'Tăng dần' : 'Giảm dần'}`}>
                            <IconButton onClick={toggleSort} sx={{ border: '1px solid #ced4da', borderRadius: 1, height: '40px', color: '#007373', bgcolor: 'white', marginBottom: '12px' }}>
                                <SortIcon sx={{ transform: sortOrder === 'desc' ? 'scaleY(-1)' : 'none' }} />
                            </IconButton>
                        </Tooltip>
                    </Box>
                    <Box sx={{ pt: 3.2 }}>
                        <Button variant="contained" onClick={handleApplyFilter} sx={{ bgcolor: '#007373', '&:hover': { bgcolor: '#005c5c' }, height: '40px', px: 3, borderRadius: 2, whiteSpace: 'nowrap', marginBottom: '12px' }}>
                            Áp dụng bộ lọc
                        </Button>
                    </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                    <Box>
                        <FormControlLabel 
                            control={<Checkbox checked={isAllTime} onChange={(e) => { setIsAllTime(e.target.checked); if(e.target.checked) setQuickFilterMode('CUSTOM'); }} sx={{ color: '#007373', '&.Mui-checked': { color: '#007373' }, p: 0.5 }} />} 
                            label={<Typography variant="body2" fontWeight="bold">Toàn thời gian</Typography>} 
                            sx={{ m: 0, mt: 2.5 }} 
                        />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: '300px', borderLeft: '1px solid #eee', pl: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <CustomLabel title="Trạng thái ĐH Xe" />
                            <Tooltip title={filterTransportStatus.length === Object.keys(TRANSPORT_OPS_LABELS).length ? "Bỏ chọn tất cả" : "Chọn tất cả"}>
                                <IconButton size="small" onClick={handleToggleAllTransportStatus} sx={{ padding: 0, color: '#007373' }}>
                                    {filterTransportStatus.length === Object.keys(TRANSPORT_OPS_LABELS).length ? <RemoveDoneIcon fontSize="small" /> : <DoneAllIcon fontSize="small" />}
                                </IconButton>
                            </Tooltip>
                        </Box>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                            {Object.keys(TRANSPORT_OPS_LABELS).map((key) => (
                                <FormControlLabel
                                    key={key}
                                    control={<Checkbox checked={filterTransportStatus.indexOf(key) > -1} onChange={(e) => setFilterTransportStatus(prev => e.target.checked ? [...prev, key] : prev.filter(k => k !== key))} size="small" sx={{ color: '#007373', '&.Mui-checked': { color: '#007373' }, p: 0.5 }} />}
                                    label={<Typography variant="body2">{TRANSPORT_OPS_LABELS[key]}</Typography>}
                                    sx={{ mr: 0, ml: 0 }}
                                />
                            ))}
                        </Box>
                    </Box>
                </Box>
            </Box>

            {/* === THANH BULK ACTION CỦA XE === */}
            {selectedIds.length > 0 && (
                <Paper sx={{ p: 1, mb: 2, bgcolor: '#fff3e0', border: '1px solid #ed6c02', display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', ml: 2, color: '#ed6c02' }}>
                        Đang chọn: {selectedIds.length} chuyến xe
                    </Typography>
                    
                    <Button 
                        variant="contained" size="small" color="warning" 
                        onClick={handleOpenBulkTransportOrder} startIcon={<MailOutlineIcon />}
                        sx={{ fontWeight: 'bold' }}
                    >
                        Tạo lệnh đặt xe / Gửi Mail
                    </Button>

                    <Divider orientation="vertical" flexItem />

                    <Typography variant="caption" sx={{ fontWeight: 'bold' }}>Cập nhật trạng thái ĐH:</Typography>
                    <Stack direction="row" spacing={1}>
                        {['CONTACTED', 'CONFIRMED', 'DONE'].map(status => (
                            <Button 
                                key={status} size="small" variant="contained" 
                                sx={{ 
                                    bgcolor: 'white', color: theme => theme.palette[TRANSPORT_OPS_COLORS[status]].main,
                                    border: '1px solid', borderColor: theme => theme.palette[TRANSPORT_OPS_COLORS[status]].main,
                                    '&:hover': { bgcolor: theme => theme.palette[TRANSPORT_OPS_COLORS[status]].main, color: 'white' }
                                }}
                                onClick={() => handleBulkUpdateStatus(status)} 
                            >
                                {TRANSPORT_OPS_LABELS[status]}
                            </Button>
                        ))}
                    </Stack>
                </Paper>
            )}

            {/* === BẢNG DỮ LIỆU === */}
            <DataTable
                maxHeight="65vh"
                columns={[
                    {
                        label: (
                            <Checkbox color="warning" sx={{ p: 0, color: 'white' }}
                                indeterminate={selectedIds.length > 0 && selectedIds.length < displayData.length}
                                checked={displayData.length > 0 && selectedIds.length === displayData.length}
                                onChange={handleSelectAll}
                            />
                        ),
                        minWidth: 50, align: 'center'
                    },
                    { label: 'Mã BK', minWidth: 100 },
                    { label: 'Khách hàng', minWidth: 150 },
                    { label: 'Nhà xe', minWidth: 180 },
                    { label: 'Chiều xe', minWidth: 100 },
                    { label: 'Loại xe', minWidth: 120 },
                    { label: 'Thông tin Đón/Trả', minWidth: 250 },
                    { label: 'Trạng thái ĐH', minWidth: 120 },
                    { label: 'Thao tác', align: 'center', minWidth: 100 },
                ]}
                data={displayData}
                renderRow={(row) => (
                    <TableRow key={row.id} hover>
                        <TableCell padding="checkbox" align="center">
                            <Checkbox color="warning" checked={selectedIds.includes(row.id)} onChange={(e) => { e.stopPropagation(); handleSelectRow(row.id); }} />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 'bold', color: '#007373' }}>{row.booking_code || '---'}</TableCell>
                        <TableCell>
                            <Typography variant="body2" fontWeight="bold">{row.customer_name || 'Khách lẻ'}</Typography>
                            <Typography variant="caption" color="textSecondary">{row.customer_phone || ''}</Typography>
                        </TableCell>
                        <TableCell>
                            <Typography variant="body2" fontWeight="bold">
                                {row.supplier_name || 'Chưa gán nhà xe'}
                            </Typography>
                        </TableCell>
                        <TableCell>
                            <Chip label={DIRECTION_LABELS[row.direction] || row.direction} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell>{row.vehicle_type_display || row.vehicle_type}</TableCell>
                        <TableCell>
                            <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}><strong>Đón:</strong> {row.pickup_point || 'Chưa rõ'}</Typography>
                            <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}><strong>Trả:</strong> {row.dropoff_point || 'Chưa rõ'}</Typography>
                            <Typography variant="caption" sx={{ color: '#d32f2f', fontWeight: 'bold' }}>
                                ⏰ {row.pickup_datetime ? formatDateDisplay(row.pickup_datetime) : 'Chưa cập nhật giờ'}
                            </Typography>
                        </TableCell>
                        <TableCell>
                            <Chip 
                                label={TRANSPORT_OPS_LABELS[row.operation_status || 'OPEN']} 
                                color={TRANSPORT_OPS_COLORS[row.operation_status || 'OPEN']} 
                                size="small"
                            />
                        </TableCell>
                        <TableCell align="center">
                            <Button size="small" variant="outlined" color="warning" sx={{ fontSize: '0.7rem', py: 0.5, textTransform: 'none', bgcolor: 'white' }} onClick={() => handleViewBooking(row.booking_code)}>
                                Xem BK
                            </Button>
                        </TableCell>
                    </TableRow>
                )}
            />
            
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#007373' }}>
                    Tổng: {displayData.length} chuyến xe phát sinh riêng
                </Typography>
            </Box>

            {/* --- CÁC DIALOG KÈM THEO --- */}
            <TransportOrderDialog 
                open={isTransportOrderOpen}
                onClose={() => setIsTransportOrderOpen(false)}
                selectedTransports={selectedTransportsForOrder}
                showToast={showToast}
                onSuccess={() => { setSelectedIds([]); if(onReload) onReload(); }}
            />

            <ConfirmDialog 
                open={confirmModal.open}
                title={confirmModal.title}
                content={confirmModal.content}
                onClose={closeConfirmModal}
                onConfirm={handleConfirmAction}
                confirmColor={confirmModal.confirmColor}
            />
        </Paper>
    );
};
export default TransportOpsTab;